import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";

export const CustomersCard = ({
  count,
  percentageValue
}: {
  count: number;
  percentageValue: number;
}) => {
  const navigate = useNavigate();

  // Calculate the difference from previous day
  const calculateDifference = () => {
    if (count === 0) return 0;
    // If percentageValue is positive, calculate: current - previous
    // previous = current / (1 + percentage/100)
    const previousValue = count / (1 + percentageValue / 100);
    return Math.round(Math.abs(count - previousValue));
  };

  const difference = calculateDifference();

  // Determine the message based on scenarios
  const getMessage = () => {
    if (count === 0) {
      return "No new customers yesterday — time to reach out to prospects!";
    } else if (percentageValue > 0) {
      return `${count} new customers joined yesterday — that's ${difference} more than the day before! Great work!`;
    } else if (percentageValue < 0) {
      return `${count} new customers joined yesterday — that's ${difference} fewer than the day before, but still solid progress.`;
    } else {
      return `${count} new customers joined yesterday — same as the day before.`;
    }
  };

  return (
    <div className="group relative px-5 py-3.5 rounded-[20px] bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm border border-border/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(18,128,121,0.15)] transition-all duration-300 hover:scale-[1.03] overflow-hidden">
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-2xl">👥</span>
          <h3 className="text-sm font-medium text-muted-foreground">
            New Customers Yesterday
          </h3>
        </div>

        <div className="mb-1.5">
          <p className="text-3xl font-bold text-foreground tracking-tight">
            {count} new customer{count !== 1 ? "s" : ""}
          </p>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          {getMessage()}
        </p>

        {/* Avatar cluster - Dynamic based on customer count */}
        {count > 0 && (
          <div className="flex items-center -space-x-2 mt-1">
            {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
              <div
                key={i}
                className="relative w-8 h-8 rounded-full bg-gradient-to-br from-teal-200/80 to-teal-100/60 border-2 border-white shadow-sm flex items-center justify-center flex-shrink-0"
                style={{
                  animationDelay: `${i * 100}ms`,
                  zIndex: 5 - i
                }}
              >
                <span className="text-xs font-semibold text-teal-700">
                  {String.fromCharCode(65 + i)}
                </span>
              </div>
            ))}
            {count > 5 && (
              <div className="relative w-8 h-8 rounded-full bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center flex-shrink-0 text-[10px] font-semibold text-gray-600">
                +{count - 5}
              </div>
            )}
          </div>
        )}
        {/* View More Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate("/reports/customers/overview");
          }}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors group/btn mb-3"
        >
          View More
          <ArrowRight className="w-3 h-3 transition-transform group-hover/btn:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};
